import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Edit, Download, ChevronDown } from "lucide-react";
import jsPDF from "jspdf";

type MedicalStatus = Database["public"]["Enums"]["medical_status"];
type ComputerNumberStatus = Database["public"]["Enums"]["computer_number_status"];

interface StudentDetailsEditorProps {
  studentId: string;
}

interface StudentEditableData {
  first_name: string;
  last_name: string;
  office_name: string;
  calling_name: string;
  phone: string;
  passport_number: string;
  gender: string;
  computer_number: string;
  egca_id: string;
  computer_number_status: ComputerNumberStatus;
  class_1_medical: MedicalStatus;
  class_2_medical: MedicalStatus;
  uniform_measurements: UniformMeasurements | null;
  batch_id: string | null;
}

interface UniformMeasurements {
  shirt_length?: string;
  shirt_shoulder?: string;
  shirt_sleeve?: string;
  shirt_chest?: string;
  shirt_waist?: string;
  shirt_seat?: string;
  shirt_neck?: string;
  shirt_full_sleeve?: boolean;
  pant_length?: string;
  pant_waist?: string;
  pant_seat?: string;
  pant_thighs_loose?: string;
  pant_thighs_tight?: string;
  pant_bottom?: string;
}

const StudentDetailsEditor = ({ studentId }: StudentDetailsEditorProps) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showUniformMeasurements, setShowUniformMeasurements] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [batches, setBatches] = useState<Array<{ id: string; name: string }>>([]);
  const [data, setData] = useState<StudentEditableData>({
    first_name: "",
    last_name: "",
    office_name: "",
    calling_name: "",
    phone: "",
    passport_number: "",
    gender: "",
    computer_number: "",
    egca_id: "",
    computer_number_status: "Pending",
    class_1_medical: "Pending",
    class_2_medical: "Pending",
    uniform_measurements: null,
    batch_id: null,
  });
  const [class1AppointmentDate, setClass1AppointmentDate] = useState("");
  const [class2AppointmentDate, setClass2AppointmentDate] = useState("");
  const [flightSchoolInversion, setFlightSchoolInversion] = useState(false);

  // Uniform measurements fields
  const [measurements, setMeasurements] = useState({
    shirt_length: "",
    shirt_shoulder: "",
    shirt_sleeve: "",
    shirt_chest: "",
    shirt_waist: "",
    shirt_seat: "",
    shirt_neck: "",
    shirt_full_sleeve: false,
    pant_length: "",
    pant_waist: "",
    pant_seat: "",
    pant_thighs_loose: "",
    pant_thighs_tight: "",
    pant_bottom: "",
  });

  useEffect(() => {
    checkAdminAccess();
    fetchStudentData();
    fetchBatches();
  }, [studentId]);

  const checkAdminAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id);

    const hasAdminAccess = roles?.some((r: any) => r.role === "admin" || r.role === "manager" || r.role === "super_admin");
    setIsAdmin(Boolean(hasAdminAccess));
  };

  const fetchBatches = async () => {
    try {
      const { data, error } = await supabase
        .from("batches")
        .select("id, name")
        .order("name");

      if (error) throw error;
      setBatches(data || []);
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  };

  const fetchStudentData = async () => {
    try {
      const { data: student, error } = await supabase
        .from("students")
        .select("first_name, last_name, office_name, calling_name, phone, passport_number, gender, computer_number, egca_id, computer_number_status, class_1_medical, class_2_medical, class1_appointment_date, class2_appointment_date, flight_school_inversion, uniform_measurements, batch_id")
        .eq("id", studentId)
        .maybeSingle();

      if (error) throw error;
      
      if (!student) {
        toast.error("Student not found");
        return;
      }
      
      setData({
        first_name: student.first_name || "",
        last_name: student.last_name || "",
        office_name: student.office_name || "",
        calling_name: student.calling_name || "",
        phone: student.phone || "",
        passport_number: student.passport_number || "",
        gender: student.gender || "",
        computer_number: student.computer_number || "",
        egca_id: student.egca_id || "",
        computer_number_status: (student.computer_number_status as ComputerNumberStatus) || "Pending",
        class_1_medical: (student.class_1_medical as MedicalStatus) || "Pending",
        class_2_medical: (student.class_2_medical as MedicalStatus) || "Pending",
        uniform_measurements: student.uniform_measurements as UniformMeasurements | null,
        batch_id: student.batch_id,
      });
      setClass1AppointmentDate((student as any).class1_appointment_date || "");
      setClass2AppointmentDate((student as any).class2_appointment_date || "");
      setFlightSchoolInversion((student as any).flight_school_inversion || false);

      if (student.uniform_measurements) {
        const uniformData = student.uniform_measurements as UniformMeasurements;
        setMeasurements({
          shirt_length: uniformData.shirt_length || "",
          shirt_shoulder: uniformData.shirt_shoulder || "",
          shirt_sleeve: uniformData.shirt_sleeve || "",
          shirt_chest: uniformData.shirt_chest || "",
          shirt_waist: uniformData.shirt_waist || "",
          shirt_seat: uniformData.shirt_seat || "",
          shirt_neck: uniformData.shirt_neck || "",
          shirt_full_sleeve: uniformData.shirt_full_sleeve || false,
          pant_length: uniformData.pant_length || "",
          pant_waist: uniformData.pant_waist || "",
          pant_seat: uniformData.pant_seat || "",
          pant_thighs_loose: uniformData.pant_thighs_loose || "",
          pant_thighs_tight: uniformData.pant_thighs_tight || "",
          pant_bottom: uniformData.pant_bottom || "",
        });
      }
    } catch (error) {
      console.error("Error fetching student data:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const hasUniformData = Object.entries(measurements).some(([key, value]) => 
        key !== 'shirt_full_sleeve' && value !== "" && value !== false
      );

      const { error } = await supabase
        .from("students")
        .update({
          first_name: data.first_name.trim(),
          last_name: data.last_name.trim() || null,
          office_name: data.office_name.trim(),
          calling_name: data.calling_name.trim() || null,
          phone: data.phone.trim() || null,
          passport_number: data.passport_number.trim() || null,
          gender: data.gender.trim() || null,
          computer_number: data.computer_number.trim() || null,
          egca_id: data.egca_id.trim() || null,
          computer_number_status: data.computer_number_status,
          class_1_medical: data.class_1_medical,
          class_2_medical: data.class_2_medical,
          class1_appointment_date: class1AppointmentDate || null,
          class2_appointment_date: class2AppointmentDate || null,
          flight_school_inversion: flightSchoolInversion,
          uniform_measurements: hasUniformData ? measurements : null,
          batch_id: data.batch_id,
        })
        .eq("id", studentId);

      if (error) throw error;
      toast.success("Student details updated successfully!");
      setEditing(false);
      fetchStudentData();
    } catch (error: any) {
      toast.error(error.message || "Failed to update student details");
    } finally {
      setSaving(false);
    }
  };

  const medicalStatuses = ["Done", "Pending", "Appointment Scheduled", "Expired", "Test Done", "Retest"];
  const computerNumberStatuses = ["Verification Pending", "Applied", "Approved", "Pending"];

  const handleDownloadMeasurements = async () => {
    const fullName = `${data.first_name} ${data.last_name}`.trim();
    
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Add title
    pdf.setFontSize(18);
    pdf.text(`Uniform Measurements`, 105, 20, { align: "center" });
    
    pdf.setFontSize(14);
    pdf.text(fullName, 105, 30, { align: "center" });
    
    pdf.setFontSize(10);
    let yPos = 45;
    
    // Shirt measurements
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text("Shirt Measurements:", 20, yPos);
    yPos += 8;
    
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);
    pdf.text(`Length: ${measurements.shirt_length || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Shoulder: ${measurements.shirt_shoulder || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Sleeve: ${measurements.shirt_sleeve || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Chest: ${measurements.shirt_chest || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Waist: ${measurements.shirt_waist || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Seat: ${measurements.shirt_seat || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Neck: ${measurements.shirt_neck || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Full Sleeve: ${measurements.shirt_full_sleeve ? 'Yes' : 'No'}`, 30, yPos);
    yPos += 12;
    
    // Pant measurements
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text("Pant Measurements:", 20, yPos);
    yPos += 8;
    
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);
    pdf.text(`Length: ${measurements.pant_length || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Waist: ${measurements.pant_waist || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Seat: ${measurements.pant_seat || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Thighs (Loose): ${measurements.pant_thighs_loose || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Thighs (Tight): ${measurements.pant_thighs_tight || 'N/A'}`, 30, yPos);
    yPos += 6;
    pdf.text(`Bottom: ${measurements.pant_bottom || 'N/A'}`, 30, yPos);

    pdf.save(`${fullName.replace(/\s+/g, '_')}_measurements.pdf`);
    toast.success("Measurements downloaded as PDF");
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg sm:text-xl">Student Details</CardTitle>
        {!editing && isAdmin && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Details
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              value={data.first_name}
              onChange={(e) => setData({ ...data, first_name: e.target.value })}
              disabled={!editing}
              placeholder="Enter first name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="surname">Surname</Label>
            <Input
              id="surname"
              value={data.last_name}
              onChange={(e) => setData({ ...data, last_name: e.target.value })}
              disabled={!editing}
              placeholder="Optional"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="officeName">Office Name</Label>
            <Input
              id="officeName"
              value={data.office_name}
              onChange={(e) => setData({ ...data, office_name: e.target.value })}
              disabled={!editing}
              placeholder="Enter office name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="callingName">Calling Name</Label>
            <Input
              id="callingName"
              value={data.calling_name}
              onChange={(e) => setData({ ...data, calling_name: e.target.value })}
              disabled={!editing}
              placeholder="Leave empty to use office name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={data.phone}
              onChange={(e) => setData({ ...data, phone: e.target.value })}
              disabled={!editing}
              placeholder="Enter phone number"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="passport">Passport Number</Label>
            <Input
              id="passport"
              value={data.passport_number}
              onChange={(e) => setData({ ...data, passport_number: e.target.value })}
              disabled={!editing}
              placeholder="Enter passport number"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gender">Gender</Label>
            <Select
              value={data.gender || "none"}
              onValueChange={(value) => setData({ ...data, gender: value === "none" ? "" : value })}
              disabled={!editing}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not Specified</SelectItem>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="computerNumber">Computer Number</Label>
            <Input
              id="computerNumber"
              value={data.computer_number}
              onChange={(e) => setData({ ...data, computer_number: e.target.value })}
              disabled={!editing}
              placeholder="Enter computer number"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="egcaId">EGCA ID</Label>
            <Input
              id="egcaId"
              value={data.egca_id}
              onChange={(e) => setData({ ...data, egca_id: e.target.value })}
              disabled={!editing}
              placeholder="Enter EGCA ID"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="batch">Batch</Label>
            <Select
              value={data.batch_id || "none"}
              onValueChange={(value) => setData({ ...data, batch_id: value === "none" ? null : value })}
              disabled={!editing}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select batch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Batch</SelectItem>
                {batches.map((batch) => (
                  <SelectItem key={batch.id} value={batch.id}>
                    {batch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="computerNumberStatus" className={data.computer_number_status === "Pending" ? "text-red-500" : ""}>
              Computer Number Status
            </Label>
            <Select
              value={data.computer_number_status}
              onValueChange={(value) => setData({ ...data, computer_number_status: value as ComputerNumberStatus })}
              disabled={!editing}
            >
              <SelectTrigger className={data.computer_number_status === "Pending" ? "border-red-500 text-red-500" : ""}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {computerNumberStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="class1Medical" className={data.class_1_medical === "Pending" ? "text-red-500" : ""}>
              Class 1 Medical Status
            </Label>
            <Select
              value={data.class_1_medical}
              onValueChange={(value) => setData({ ...data, class_1_medical: value as MedicalStatus })}
              disabled={!editing}
            >
              <SelectTrigger className={data.class_1_medical === "Pending" ? "border-red-500 text-red-500" : ""}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {medicalStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {data.class_1_medical === "Appointment Scheduled" && (
              <div className="mt-2">
                <Label htmlFor="class1AppointmentDate">Appointment Date</Label>
                <Input
                  id="class1AppointmentDate"
                  type="date"
                  value={class1AppointmentDate}
                  onChange={(e) => setClass1AppointmentDate(e.target.value)}
                  disabled={!editing}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="class2Medical" className={data.class_2_medical === "Pending" ? "text-red-500" : ""}>
              Class 2 Medical Status
            </Label>
            <Select
              value={data.class_2_medical}
              onValueChange={(value) => setData({ ...data, class_2_medical: value as MedicalStatus })}
              disabled={!editing}
            >
              <SelectTrigger className={data.class_2_medical === "Pending" ? "border-red-500 text-red-500" : ""}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {medicalStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {data.class_2_medical === "Appointment Scheduled" && (
              <div className="mt-2">
                <Label htmlFor="class2AppointmentDate">Appointment Date</Label>
                <Input
                  id="class2AppointmentDate"
                  type="date"
                  value={class2AppointmentDate}
                  onChange={(e) => setClass2AppointmentDate(e.target.value)}
                  disabled={!editing}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="flightSchoolInversion">Flight School Inversion</Label>
            <Select
              value={flightSchoolInversion ? "Yes" : "No"}
              onValueChange={(value) => setFlightSchoolInversion(value === "Yes")}
              disabled={!editing}
            >
              <SelectTrigger id="flightSchoolInversion">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="No">No</SelectItem>
                <SelectItem value="Yes">Yes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isAdmin && (
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowUniformMeasurements(!showUniformMeasurements)}
                className="flex items-center gap-2"
              >
                <h3 className="font-medium">Uniform Measurements</h3>
                <ChevronDown className={`h-4 w-4 transition-transform ${showUniformMeasurements ? 'rotate-180' : ''}`} />
              </Button>
              {!editing && showUniformMeasurements && (
                <Button variant="outline" size="sm" onClick={handleDownloadMeasurements}>
                  Download
                </Button>
              )}
            </div>
            
            {showUniformMeasurements && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-sm mb-3">Shirt Measurements</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="shirtLength">Length</Label>
                    <Input
                      id="shirtLength"
                      value={measurements.shirt_length}
                      onChange={(e) => setMeasurements({ ...measurements, shirt_length: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shirtShoulder">Shoulder</Label>
                    <Input
                      id="shirtShoulder"
                      value={measurements.shirt_shoulder}
                      onChange={(e) => setMeasurements({ ...measurements, shirt_shoulder: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shirtSleeve">Sleeve</Label>
                    <Input
                      id="shirtSleeve"
                      value={measurements.shirt_sleeve}
                      onChange={(e) => setMeasurements({ ...measurements, shirt_sleeve: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shirtChest">Chest</Label>
                    <Input
                      id="shirtChest"
                      value={measurements.shirt_chest}
                      onChange={(e) => setMeasurements({ ...measurements, shirt_chest: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shirtWaist">Waist</Label>
                    <Input
                      id="shirtWaist"
                      value={measurements.shirt_waist}
                      onChange={(e) => setMeasurements({ ...measurements, shirt_waist: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shirtSeat">Seat</Label>
                    <Input
                      id="shirtSeat"
                      value={measurements.shirt_seat}
                      onChange={(e) => setMeasurements({ ...measurements, shirt_seat: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shirtNeck">Neck</Label>
                    <Input
                      id="shirtNeck"
                      value={measurements.shirt_neck}
                      onChange={(e) => setMeasurements({ ...measurements, shirt_neck: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2 flex items-center gap-2 pt-6">
                    <input
                      type="checkbox"
                      id="shirtFullSleeve"
                      checked={measurements.shirt_full_sleeve}
                      onChange={(e) => setMeasurements({ ...measurements, shirt_full_sleeve: e.target.checked })}
                      disabled={!editing}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="shirtFullSleeve" className="cursor-pointer">Full Sleeve (for girls)</Label>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-sm mb-3">Pant Measurements</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pantLength">Length</Label>
                    <Input
                      id="pantLength"
                      value={measurements.pant_length}
                      onChange={(e) => setMeasurements({ ...measurements, pant_length: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pantWaist">Waist</Label>
                    <Input
                      id="pantWaist"
                      value={measurements.pant_waist}
                      onChange={(e) => setMeasurements({ ...measurements, pant_waist: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pantSeat">Seat</Label>
                    <Input
                      id="pantSeat"
                      value={measurements.pant_seat}
                      onChange={(e) => setMeasurements({ ...measurements, pant_seat: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pantThighsLoose">Thighs (Loose)</Label>
                    <Input
                      id="pantThighsLoose"
                      value={measurements.pant_thighs_loose}
                      onChange={(e) => setMeasurements({ ...measurements, pant_thighs_loose: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pantThighsTight">Thighs (Tight)</Label>
                    <Input
                      id="pantThighsTight"
                      value={measurements.pant_thighs_tight}
                      onChange={(e) => setMeasurements({ ...measurements, pant_thighs_tight: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pantBottom">Bottom</Label>
                    <Input
                      id="pantBottom"
                      value={measurements.pant_bottom}
                      onChange={(e) => setMeasurements({ ...measurements, pant_bottom: e.target.value })}
                      disabled={!editing}
                      placeholder="cm"
                    />
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        )}

        {editing && (
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setEditing(false);
                fetchStudentData();
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StudentDetailsEditor;
